import { get } from "lodash";

export const NO_SOURCE_VALUE = "NONE";

export const isOpenSource = (source, now = new Date()) => {
  if (!source || source.archivedAt) return false;
  const nowTime = now.getTime();
  const startTime = source.startDate
    ? new Date(source.startDate).getTime()
    : null;
  if (startTime !== null && nowTime < startTime) return false;
  const endTime = source.endDate ? new Date(source.endDate).getTime() : null;
  if (endTime !== null && nowTime > endTime) return false;
  return true;
};

const toOption = ({ id, name }) => ({ value: id, label: name });

/**
 * "No source" first, then the sources still open for attribution. The
 * project's current source stays listed even once closed or stopped.
 */
export const sourceOptions = ({ sources, current, noSourceLabel }) => {
  const options = (sources || []).map(toOption);
  const currentId = get(current, "id");
  const withCurrent =
    currentId && !options.some(({ value }) => value === currentId)
      ? [...options, toOption(current)]
      : options;
  return [{ value: NO_SOURCE_VALUE, label: noSourceLabel }, ...withCurrent];
};

/**
 * The project keeps its own source; "no source" is a valid choice, so the
 * tracer source is not read back here.
 */
export const sourceValueOf = (project) =>
  get(project, "source.id") || NO_SOURCE_VALUE;

const sourceMatch = (id) => ({ source: { id } });

const tracerSourceMatch = (id) => ({
  AND: [{ source: null }, { tracer: { source: { id } } }],
});

const scopedSourceMatch = (scope) => (id) => ({
  [scope]: { source: { id } },
});

const scopedTracerSourceMatch = (scope) => (id) => ({
  AND: [
    { [scope]: { source: null } },
    { [scope]: { tracer: { source: { id } } } },
  ],
});

/**
 * Matches the source set on the project. A project without one falls back
 * to the source of its tracer. `scopedToProject` nests that match under
 * `project` for quote and subscription filters. `scope` nests it under
 * another relation, such as `projects` on a contact.
 */
export const projectSourceWhere = (sourceIds, options = {}) => {
  const ids = (sourceIds || []).filter(Boolean);
  if (!ids.length) return null;
  const scope = options.scope || (options.scopedToProject ? "project" : null);
  const match = scope ? scopedSourceMatch(scope) : sourceMatch;
  const fallback = scope ? scopedTracerSourceMatch(scope) : tracerSourceMatch;
  return {
    OR: ids.flatMap((id) => [match(id), fallback(id)]),
  };
};
